import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Cancel01Icon,
  CustomerSupportIcon,
  PencilEdit02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { useAuthContext } from "providers/Auth";
import { BOOM_HELP_Z_CLASS } from "softphone/stacking";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { IconAI } from "shared/components/ai/IconAI";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { Show } from "shared/components/helpers/Show";
import { cx } from "shared/utils";
import { MESSAGE_POPUP_DRAWER_CLASS } from "shared/app/MessagePopup/constants";
import { useMessagePopupStore } from "shared/app/MessagePopup/store";
import { BOOM_HELP_COPY, suggestedQuestionsFor } from "./constants";
import { useBoomHelpStore } from "./store";
import {
  useHandOffToSupport,
  usePendingReplies,
  useSendBoomHelpMessage
} from "./use-boom-help-actions";
import { Composer } from "./components/Composer";
import { EmptyState } from "./components/EmptyState";
import {
  HelpRow,
  MessageItem,
  TalkToSupportButton,
  WritingIndicator
} from "./components/MessageItem";

const isInFlight = (status: string) =>
  status === "pending" || status === "streaming";

const HandoffNotice = () => (
  <div className="flex items-start gap-3 rounded-lg border border-alpha-100 bg-background-900 px-4 py-3">
    <HugeiconsIcon
      icon={CustomerSupportIcon}
      size={20}
      className="mt-0.5 shrink-0 text-primary-500"
    />
    <div className="space-y-1">
      <p className="text-sm font-bold text-content-1000">
        {BOOM_HELP_COPY.handedOffTitle}
      </p>
      <p className="text-sm text-content-500">{BOOM_HELP_COPY.handedOff}</p>
    </div>
  </div>
);

export const BoomHelpPanel = () => {
  const { auth } = useAuthContext();
  const location = useLocation();
  const {
    isOpen,
    close,
    bindUser,
    messages,
    consecutiveThumbsDown,
    handedOff,
    newConversation
  } = useBoomHelpStore();
  const send = useSendBoomHelpMessage();
  const handOff = useHandOffToSupport();
  const bottomRef = useRef<HTMLDivElement>(null);
  usePendingReplies();

  useEffect(() => {
    bindUser(auth?.id ?? null);
  }, [auth?.id, bindUser]);

  const messagePopupTarget = useMessagePopupStore((state) => state.target);
  useEffect(() => {
    if (messagePopupTarget) close();
  }, [messagePopupTarget, close]);

  const lastContent = messages[messages.length - 1]?.content;
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, lastContent, handedOff]);

  const isEmpty = messages.length === 0 && !send.isPending;
  const waiting =
    send.isPending || messages.some((message) => isInFlight(message.status));
  const lastUserMessage = [...messages]
    .reverse()
    .find((message) => message.role === "user");

  return (
    <Show if={isOpen}>
      <aside
        role="complementary"
        aria-label={BOOM_HELP_COPY.title}
        className={cx(MESSAGE_POPUP_DRAWER_CLASS, BOOM_HELP_Z_CLASS)}
      >
        <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-alpha-100 bg-white px-6">
          <h2 className="flex shrink-0 items-center gap-2 whitespace-nowrap text-xl font-bold leading-8 text-content-1000">
            <IconAI bgSize={21} />
            {BOOM_HELP_COPY.title}
          </h2>
          <div className="flex shrink-0 items-center gap-2">
            <Show if={!isEmpty}>
              <Button
                variant={ButtonVariant.TEXT}
                size={ButtonSize.SM}
                HugeIcon={PencilEdit02Icon}
                disabled={waiting}
                onClick={newConversation}
                className="hidden items-center gap-1.5 md:inline-flex"
              >
                {BOOM_HELP_COPY.newConversation}
              </Button>
              <IconButton
                aria-label={BOOM_HELP_COPY.newConversation}
                tooltip={BOOM_HELP_COPY.newConversation}
                HugeIcon={PencilEdit02Icon}
                variant={IconButtonVariant.TEXT}
                size={IconButtonSize.SM}
                disabled={waiting}
                onClick={newConversation}
                className="!text-primary-500 md:hidden"
              />
            </Show>
            <IconButton
              aria-label={BOOM_HELP_COPY.close}
              HugeIcon={Cancel01Icon}
              variant={IconButtonVariant.TEXT}
              size={IconButtonSize.LG}
              onClick={close}
              className="!text-content-1000"
            />
          </div>
        </header>

        <div className="hide-scroll flex-1 overflow-y-auto bg-white px-6 py-4">
          <Show if={isEmpty}>
            <EmptyState
              suggestions={suggestedQuestionsFor(location.pathname)}
              disabled={waiting}
              onSelect={(question) => send.mutate(question)}
            />
          </Show>
          <Show if={!isEmpty}>
            <div className="space-y-5">
              {messages.map((message) => (
                <MessageItem
                  key={message.id}
                  message={message}
                  handingOff={handOff.isPending}
                  onHandOff={(reason) => handOff.mutate(reason)}
                  onRetry={() => send.mutate(lastUserMessage?.content ?? "")}
                />
              ))}
              <Show if={send.isPending}>
                <WritingIndicator />
              </Show>
              <Show if={consecutiveThumbsDown >= 2 && !handedOff}>
                <HelpRow label={BOOM_HELP_COPY.title}>
                  <p className="text-sm leading-6 text-content-1000">
                    {BOOM_HELP_COPY.tooManyDown}
                  </p>
                  <div className="flex">
                    <TalkToSupportButton
                      loading={handOff.isPending}
                      onClick={() => handOff.mutate("thumbs_down")}
                    />
                  </div>
                </HelpRow>
              </Show>
              <Show if={handedOff}>
                <HandoffNotice />
              </Show>
            </div>
          </Show>
          <div ref={bottomRef} />
        </div>

        <footer className="shrink-0 border-t border-alpha-100 bg-white px-6 py-3">
          <Composer
            onSend={(content) => send.mutate(content)}
            onTalkToSupport={() => handOff.mutate("user_clicked")}
            disabled={waiting}
            handingOff={handOff.isPending}
          />
        </footer>
      </aside>
    </Show>
  );
};
